import React from "react";

import { Switch } from "src/base-components/Switch";
import { ApiKeyType } from "src/clients/taktile-api";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";

type Props = {
  value: Nullable<ApiKeyType>[];
  onChange: (value: Nullable<ApiKeyType>[]) => void;
};

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="flex items-start justify-between font-inter-semibold-14px text-gray-700">
    {children}
  </label>
);

export const KeyTypeSelector: React.FC<Props> = ({ value, onChange }) => {
  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();
  const isChecked = (type: ApiKeyType) => value?.includes(type) ?? false;
  const isCasesChecked = isChecked(ApiKeyType.CASE_MANAGEMENT_CASES);
  const isWsEditorChecked = isChecked(ApiKeyType.WORKSPACE_EDITOR);

  const isDecisionType = (type: ApiKeyType) =>
    type === ApiKeyType.SANDBOX || type === ApiKeyType.LIVE;

  const handleChange = (type: ApiKeyType) => {
    if (isChecked(type)) {
      // Prevent toggling off Sandbox or Live while Cases is on
      if (isCasesChecked && isDecisionType(type)) return;

      // When disabling Editor, keep Viewer as-is
      onChange(value.filter((t) => t !== type));
      return;
    }

    // When enabling Cases, also enable Sandbox and Live
    if (type === ApiKeyType.CASE_MANAGEMENT_CASES) {
      const typesToAdd = [type, ApiKeyType.SANDBOX, ApiKeyType.LIVE].filter(
        (t) => !isChecked(t),
      );
      onChange([...value, ...typesToAdd]);
      return;
    }

    // When enabling Editor, remove redundant Viewer (editor inherits viewer)
    if (type === ApiKeyType.WORKSPACE_EDITOR) {
      onChange([
        ...value.filter((t) => t !== ApiKeyType.WORKSPACE_VIEWER),
        type,
      ]);
      return;
    }

    onChange([...value, type]);
  };

  return (
    <div className="flex flex-col gap-3">
      <Label>
        <div className="flex-1 pr-4">
          <div>Sandbox decisions</div>
          <p className="font-inter-normal-12px text-gray-600">
            Can make
            <span className="mx-1 rounded bg-gray-100 px-1 font-fira-code">
              /run/.../sandbox/decide
            </span>
            calls to either draft or published flow versions, and run Jobs in
            the sandbox environment.
          </p>
        </div>
        <Switch
          dataLoc="sandbox"
          disabled={isCasesChecked}
          enabled={isChecked(ApiKeyType.SANDBOX)}
          onChange={() => handleChange(ApiKeyType.SANDBOX)}
        />
      </Label>

      <Label>
        <div className="flex-1 pr-4">
          <div>Live decisions</div>
          <p className="font-inter-normal-12px text-gray-600">
            Can make
            <span className="mx-1 rounded bg-gray-100 px-1 font-fira-code">
              /run/.../decide
            </span>
            calls to published flow versions, and run Jobs in the live
            environment.
          </p>
        </div>
        <Switch
          dataLoc="production"
          disabled={isCasesChecked}
          enabled={isChecked(ApiKeyType.LIVE)}
          onChange={() => handleChange(ApiKeyType.LIVE)}
        />
      </Label>

      <Label>
        <div className="flex-1 pr-4">
          <div>Job management</div>
          <p className="font-inter-normal-12px text-gray-600">
            Can manage Jobs and make calls to the{" "}
            <span className="mx-1 rounded bg-gray-100 px-1 font-fira-code">
              /jobs
            </span>{" "}
            API. Note that in order to run Jobs, this API key must also have the
            sandbox or live decisions key permission.
          </p>
        </div>
        <Switch
          dataLoc="jobs"
          enabled={isChecked(ApiKeyType.JOB_ADMIN)}
          onChange={() => handleChange(ApiKeyType.JOB_ADMIN)}
        />
      </Label>

      <Label>
        <div className="flex-1 pr-4">
          <div>Decision history management</div>
          <p className="font-inter-normal-12px text-gray-600">
            Can manage decision history and make calls to the{" "}
            <span className="mx-1 rounded bg-gray-100 px-1 font-fira-code">
              /history
            </span>{" "}
            API.
          </p>
        </div>
        <Switch
          dataLoc="history"
          enabled={isChecked(ApiKeyType.HISTORY_ADMIN)}
          onChange={() => handleChange(ApiKeyType.HISTORY_ADMIN)}
        />
      </Label>

      <Label>
        <div className="flex-1 pr-4">
          <div>Audit Logs</div>
          <p className="font-inter-normal-12px text-gray-600">
            Can access audit logs and system activity.
          </p>
        </div>
        <Switch
          dataLoc="audit_logs"
          enabled={isChecked(ApiKeyType.AUDIT_LOGS)}
          onChange={() => handleChange(ApiKeyType.AUDIT_LOGS)}
        />
      </Label>

      <Label>
        <div className="flex-1 pr-4">
          <div>Workspace read-only</div>
          <p className="font-inter-normal-12px text-gray-600">
            Grants read-only access equivalent to a workspace viewer user role.
            Can view flows, connections, jobs, and workspace configuration.
          </p>
        </div>
        <Switch
          dataLoc="workspace_viewer"
          disabled={isWsEditorChecked}
          enabled={isChecked(ApiKeyType.WORKSPACE_VIEWER)}
          onChange={() => handleChange(ApiKeyType.WORKSPACE_VIEWER)}
        />
      </Label>

      <Label>
        <div className="flex-1 pr-4">
          <div>Workspace read-write</div>
          <p className="font-inter-normal-12px text-gray-600">
            Grants edit access equivalent to a workspace editor user role.
            Includes all viewer permissions plus the ability to modify flows,
            sandbox routing, and jobs.
          </p>
        </div>
        <Switch
          dataLoc="workspace_editor"
          enabled={isChecked(ApiKeyType.WORKSPACE_EDITOR)}
          onChange={() => handleChange(ApiKeyType.WORKSPACE_EDITOR)}
        />
      </Label>

      {isCaseManagementEnabled && (
        <>
          <Label>
            <div className="flex-1 pr-4">
              <div>Cases</div>
              <p className="font-inter-normal-12px text-gray-600">
                Can read and manage cases via the Case Management API. Note that
                this also includes the sandbox or live decisions key permission.
              </p>
            </div>
            <Switch
              dataLoc="cases"
              enabled={isChecked(ApiKeyType.CASE_MANAGEMENT_CASES)}
              onChange={() => handleChange(ApiKeyType.CASE_MANAGEMENT_CASES)}
            />
          </Label>

          <Label>
            <div className="flex-1 pr-4">
              <div>Case queues</div>
              <p className="font-inter-normal-12px text-gray-600">
                Can read and manage the creation and editing of queues.
              </p>
            </div>
            <Switch
              dataLoc="case_queues"
              enabled={isChecked(ApiKeyType.CASE_MANAGEMENT_QUEUES)}
              onChange={() => handleChange(ApiKeyType.CASE_MANAGEMENT_QUEUES)}
            />
          </Label>

          <Label>
            <div className="flex-1 pr-4">
              <div>Case templates</div>
              <p className="font-inter-normal-12px text-gray-600">
                Can read and manage case templates.
              </p>
            </div>
            <Switch
              dataLoc="case_templates"
              enabled={isChecked(ApiKeyType.CASE_MANAGEMENT_TEMPLATES)}
              onChange={() =>
                handleChange(ApiKeyType.CASE_MANAGEMENT_TEMPLATES)
              }
            />
          </Label>
        </>
      )}
    </div>
  );
};
